import { Slot } from 'radix-ui';
import type { ReactNode } from 'react';
import { Button } from '../../components/ui/button';
import { Icon } from '../icon/Icon';
import { AccountMenu, type AccountMenuItem } from './AccountMenu';
import { BannerInbox, BannerInboxBellIcon, type BannerInboxItem } from './BannerInbox';
import { HeaderActions } from './HeaderActions';
import { HeaderIconButton } from './HeaderIconButton';
import { HeaderIconLink } from './HeaderIconLink';
import { HeaderLogo } from './HeaderLogo';
import { HeaderNavLink } from './HeaderNavLink';
import { HeaderSeparator } from './HeaderSeparator';
import { Header } from './header';

export interface SiteHeaderUser {
  name?: string;
  email?: string;
}

export interface SiteHeaderAccountMenu {
  ariaLabel: string;
  searchPlaceholder?: string;
  items: AccountMenuItem[];
  triggerAriaLabel: string;
}

export type SiteHeaderLogin = { label: string } & ({ onClick: () => void } | { href: string; external?: boolean });

export type SiteHeaderLogo =
  | {
      href: string;
      label: string;
      current?: boolean;
      asChild?: false;
    }
  | { asChild: true; label: ReactNode };

type SiteHeaderUserState =
  | { user: null; accountMenu?: SiteHeaderAccountMenu }
  | { user: SiteHeaderUser | null; accountMenu: SiteHeaderAccountMenu };

export type SiteHeaderSupport = { url: string; label: string };

export type SiteHeaderWatchLive = (SiteHeaderSupport & { asChild?: false }) | { asChild: true; label: ReactNode };

export interface SiteHeaderBannerInbox {
  triggerAriaLabel: string;
  closeLabel: string;
  title: string;
  emptyStateLabel: string;
  items: BannerInboxItem[];
}

export type SiteHeaderProps = SiteHeaderUserState & {
  watchLive?: SiteHeaderWatchLive;
  /** Support link rendered in the actions area. Omit to hide it entirely (e.g. focused campaign flows). */
  support?: SiteHeaderSupport;
  bannerInbox?: SiteHeaderBannerInbox;
  /** Anonymous-state login affordance. Omit to drop the login button entirely (e.g. focused campaign flows). */
  login?: SiteHeaderLogin;
  /** When set, wraps the logo in a labelled anchor. */
  logo?: SiteHeaderLogo;
  /** Optional CTAs rendered in the anonymous state, before the login affordance. */
  anonymousCtas?: ReactNode;
  /**
   * Forwarded straight to the underlying `<header>` element (no wrapping node) — a sticky element
   * needs a parent taller than itself to have any room to stick, so callers must never wrap this
   * component to apply a class (e.g. a theme scope); pass it here instead.
   */
  className?: string;
};

export function SiteHeader({ watchLive, support, bannerInbox, user, login, accountMenu, logo, anonymousCtas, className }: SiteHeaderProps) {
  return (
    <Header className={className}>
      {logo ? (
        logo.asChild ? (
          <Slot.Root className="inline-flex cursor-pointer rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
            {logo.label}
          </Slot.Root>
        ) : (
          <a
            href={logo.href}
            aria-label={logo.label}
            aria-current={logo.current ? 'page' : undefined}
            className="inline-flex cursor-pointer rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <HeaderLogo />
          </a>
        )
      ) : (
        <HeaderLogo />
      )}
      <HeaderActions>
        {watchLive ? (
          <>
            {watchLive.asChild ? (
              <HeaderNavLink asChild>{watchLive.label}</HeaderNavLink>
            ) : (
              <HeaderNavLink href={watchLive.url} icon="play-circle">
                {watchLive.label}
              </HeaderNavLink>
            )}
            <HeaderSeparator />
          </>
        ) : null}
        {support ? (
          <HeaderIconLink href={support.url} aria-label={support.label} target="_blank" rel="noopener noreferrer">
            <Icon name="circle-help" size="lg" />
          </HeaderIconLink>
        ) : null}
        {user && bannerInbox ? (
          <BannerInbox
            closeLabel={bannerInbox.closeLabel}
            title={bannerInbox.title}
            emptyStateLabel={bannerInbox.emptyStateLabel}
            items={bannerInbox.items}
          >
            <HeaderIconButton aria-label={bannerInbox.triggerAriaLabel}>
              <BannerInboxBellIcon hasNotifications={bannerInbox.items.length > 0} />
            </HeaderIconButton>
          </BannerInbox>
        ) : null}
        {user && accountMenu ? (
          <AccountMenu
            ariaLabel={accountMenu.ariaLabel}
            user={user}
            searchPlaceholder={accountMenu.searchPlaceholder}
            items={accountMenu.items}
          >
            <HeaderIconButton aria-label={accountMenu.triggerAriaLabel}>
              <Icon name="personal-data" size="lg" className="text-secondary" />
            </HeaderIconButton>
          </AccountMenu>
        ) : (
          <>
            {anonymousCtas}
            {login ? (
              'href' in login ? (
                <Button variant="ghost-dark" size="sm" asChild>
                  <a href={login.href} {...(login.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
                    {login.label}
                  </a>
                </Button>
              ) : (
                <Button variant="ghost-dark" size="sm" className="cursor-pointer" onClick={login.onClick}>
                  {login.label}
                </Button>
              )
            ) : null}
          </>
        )}
      </HeaderActions>
    </Header>
  );
}
